<template>
  <div>
    <div class="grid gap-4 md:grid-cols-2">
      <vx-card>
        <h3 class="mb-4 font-medium">Advisor Settlement Details</h3>
        <vs-row v-for="(item, i) in advisorSettlementDetailDisplayItems" :key="i" class="mb-4">
          <vs-col vs-w="5" class="font-medium">{{ item.label }}:</vs-col>
          <vs-col vs-w="7">
            <span v-if="item.isDate">{{ _.get(advisorSettlement, item.key) | moment('DD MMM YYYY, hh:mm A') }}</span>
            <span v-else-if="item.key === 'status'">
              <vs-chip :color="getStatusColor(_.get(advisorSettlement, item.key))">
                {{ _.get(advisorSettlement, item.key) }}
              </vs-chip>
            </span>
            <span v-else-if="item.key === 'amount'">
              {{ _.get(advisorSettlement, 'currency') }} {{ Number(_.get(advisorSettlement, item.key)).toFixed(2) }}
            </span>
            <span v-else>{{ _.get(advisorSettlement, item.key) || '-' }}</span>
          </vs-col>
        </vs-row>
        <vs-row class="mb-4">
          <vs-col vs-w="5" class="font-medium">Remark:</vs-col>
          <vs-col vs-w="12">
            <vs-textarea v-model="remark" />
          </vs-col>
        </vs-row>

        <vs-button v-if="_.get(advisorSettlement, 'status') !== item.value" :color="getStatusColor(item.value)" class="ml-auto mt-2 mr-2" v-for="(item) in statusOptions" @click="handleAdvisorSettlement(item.value)">{{ item.label }}</vs-button>
      </vx-card>
      <vx-card>
        <h3 class="mb-4 font-medium">Receipt</h3>
        <a v-if="receiptUrl"
          class="underline text-primary block" target="_blank" :href="receiptUrl">
          {{ receiptUrl }}
        </a>
        <vs-button v-if="receiptUrl" color="danger" class="ml-auto my-2 mr-2" @click="receiptUrl=''">Remove</vs-button>
        <drop-zone
          key='receipt'
          :text="`Upload Document`"
          :accept="`image/*, application/pdf`"
          :displayAs="displayAsType"
          :value="receiptUrl"
          @input="onReceiptSuccessUploaded"
        />
      </vx-card>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import _ from 'lodash';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import DropZone from '@/components/DropZone';

export default {
  components: {
    DropZone,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      receiptUrl: '',
      remark: '',
      displayAsType: 'attachment',
    }
  },
  computed: {
    _() {
      return _;
    },
    advisorSettlementId() {
      return this.$route.params.id || undefined;
    },
    advisorSettlementDetailDisplayItems() {
      return [
        { label: 'Status', key: 'status', isDate: false },
        { label: 'Advisor Name', key: 'advisor.name', isDate: false },
        { label: 'From Date', key: 'fromDate', isDate: true },
        { label: 'To Date', key: 'toDate', isDate: true },
        { label: 'Amount', key: 'amount', isDate: false },
      ]
    },
    statusOptions() {
      return [
        { label: 'Pending', value: 'PENDING' },
        { label: 'Approve', value: 'APPROVED' },
        { label: 'Reject', value: 'REJECT' },
        { label: 'Paid', value: 'PAID' },
      ];
    },
    getStatusLabel() {
      return value => _.find(this.statusOptions, { value }).label;
    },
    advisorSettlementReceiptUrl() {
      return this.advisorSettlement && this.advisorSettlement.receiptUrl
    },
    ...mapState({
      advisorSettlement: ({ advisorSettlement }) => advisorSettlement.advisorSettlement,
      updateAdvisorSettlementState: ({ advisorSettlement }) => advisorSettlement.apiState.updateAdvisorSettlement
    }),
  },
  methods: {
    ...mapActions({
      getAdvisorSettlementDetail: 'advisorSettlement/getAdvisorSettlement',
      updateAdvisorSettlement: 'advisorSettlement/updateAdvisorSettlement'
    }),
    async fetchData() {
      await this.getAdvisorSettlementDetail(this.advisorSettlementId).then(() => {
        this.receiptUrl = this.advisorSettlement.receiptUrl
        this.remark = this.advisorSettlement.remark
      });
    },
    handleDocumentClick(url) {
      window.open(url, '_blank');
    },
    getStatusColor(status) {
      if (status === 'PENDING') return 'warning';
      if (status === 'REJECT') return 'danger';
      if (status === 'APPROVED') return 'success';
      if (status === 'PAID') return 'primary';
      return 'primary';
    },
    onReceiptSuccessUploaded(file) {
      console.log(file)
      let receipt
      if(typeof file === 'object' && file !== null) {
        if(file && file.fileUrl && file.fileUrl.split('.').pop() === 'pdf') {
          receipt = file.fileUrl
          this.displayAsType = 'attachment'
        } else {
          receipt = file.fileUrl
          this.displayAsType = 'image'
        }
      } else {
        if(file && file.split('.').pop() === 'pdf') {
          receipt = file
          this.displayAsType = 'attachment'
        } else {
          receipt = file
          this.displayAsType = 'image'
        }
      }

      this.receiptUrl = receipt ? receipt : null
    },
    handleAdvisorSettlement(status) {
      return this.$vs.dialog({
        type:'confirm',
        color: this.getStatusColor(status),
        title: `${status} Confirmation`,
        text: `Are you sure you want to ${status} this settlement status?`,
        acceptText: 'Yes',
        accept: async () => {
          let body = {
            status: status,
            receiptUrl: this.receiptUrl,
            remark: this.remark
          }
          await this.updateAdvisorSettlement({id: this.advisorSettlementId, body: body});
        },
      });
    },
  },
  async mounted() {
    await this.fetchData();
  },
  watch: {
    'updateAdvisorSettlementState.error'(error) {
      if (error) {
        this.$vs.notify({
          title: 'Error!',
          text: error.data.message,
          color: 'danger',
          position: 'top-center',
        });

        this.$router.push({ name: 'advisorSettlement'});
      }
    },
    'updateAdvisorSettlementState.success'(value) {
      if (value) {
        this.$vs.notify({
          title: 'Successful update!',
          color: 'success',
          position: 'top-center',
        });
      }
    },
  },
};
</script>

<style lang="scss">
.vs-tabs--ul {
  box-shadow: none;
  -webkit-box-shadow: none;
  border-bottom: 2px solid #eee;
}
</style>
